// library imports
import React from "react";
import ReactDOM from "react-dom/client";
import { ConfigProvider, message, Modal, notification, theme } from "antd";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
// style imports
import "./styles/index.css";
// config imports
import { route_basename } from "./configs/configs";
// context imports
import { IndexContext } from "./context/IndexContext";
// hook imports
import useThemeSwitcher from "./hooks/useThemeSwitcher";
// component/page imports
import MainLayout from "./layout/MainLayout";
import DashboardPage from "./pages/dashboard/DashboardPage";
import ConfigurationPage from "./pages/configuration/ConfigurationPage";
import LoginPage from "./pages/login/LoginPage";
import UserProfilePage from "./pages/account/UserProfilePage";
import ChangePassword from "./pages/account/ChangePassword";
import MonitoringPage from "./pages/device/MonitoringPage";
import PowerTrendPage from "./pages/trending/PowerTrendPage";
import EnergyTrendPage from "./pages/trending/EnergyTrendPage";
import ParameterTrendPage from "./pages/trending/ParameterTrendPage";
import AlarmPage from "./pages/alarm/AlarmPage";
import OverviewReportPage from "./pages/reports/OverviewReportPage";
import BillingReportPage from "./pages/reports/BillingReportPage";
import ConfigurationSitePage from "./pages/configuration/site/ConfigurationSitePage";
import SingleLineDiagramConfigurationPage from "./pages/configuration/site/SingleLineConfigurationPage";
import { App as AntD } from "antd";
import locale from "antd/locale/en_GB";
import "dayjs/locale/en-gb";
import dayjs from "dayjs";
import SingleLineDiagramPage from "./pages/device/SingleLineDiagramPage";
dayjs.locale("en-gb");

const App = () => {
  // load theme switcher
  const [appTheme, updateTheme, isLightTheme] = useThemeSwitcher();

  // for triggering message
  const [messageApi, messageApiContext] = message.useMessage();
  // for triggering notification
  const [notificationApi, notificationApiContext] =
    notification.useNotification();
  const [modalApi, modalContext] = Modal.useModal();

  return (
    <ConfigProvider theme={{ ...appTheme, cssVar: true }} locale={locale}>
      <IndexContext.Provider
        value={{
          messageApi: messageApi,
          notificationApi: notificationApi,
          modalApi: modalApi,
          isLightTheme: isLightTheme,
          updateTheme: updateTheme,
        }}
      >
        <AntD>
          {messageApiContext}
          {notificationApiContext}
          {modalContext}
          <BrowserRouter basename={route_basename}>
            <Routes>
              {/* Login Route */}
              <Route path="login" element={<LoginPage />}></Route>
              {/* All content pages */}
              <Route element={<MainLayout updateTheme={updateTheme} />}>
                <Route path="Dashboard" element={<DashboardPage />}></Route>
                {/* Sub route for Monitoring */}
                <Route path="Device">
                  <Route path="Monitoring" element={<MonitoringPage />} />
                  <Route
                    path="SingleLineDiagram"
                    element={<SingleLineDiagramPage />}
                  />
                  <Route path="*" element={<MonitoringPage />} />
                </Route>
                {/* Sub route for Trending */}
                <Route path="Trending">
                  <Route path="*" element={<Navigate to="Power" />} />
                  <Route path="" element={<PowerTrendPage />} />
                  <Route path="Power" element={<PowerTrendPage />} />
                  <Route path="Energy" element={<EnergyTrendPage />} />
                  <Route path="Parameter" element={<ParameterTrendPage />} />
                </Route>
                {/* Sub route for Alarm */}
                <Route path="Alarm" element={<AlarmPage />}>
                  <Route path="Alarm" element={<AlarmPage />} />
                </Route>
                {/* Sub route for Reports */}
                <Route path="Reports" >
                  <Route path="Overview" element={<OverviewReportPage />} />
                  <Route path="Billing" element={<BillingReportPage />} />
                </Route>
                {/* Sub route for Configuration */}
                <Route path="Configuration">
                  <Route path="" element={<ConfigurationPage />}></Route>
                  <Route
                    path="Site/:siteID"
                    element={<ConfigurationSitePage />}
                  ></Route>
                  <Route
                    path="Site/SingleLineDiagram/:sldID"
                    element={<SingleLineDiagramConfigurationPage />}
                  ></Route>
                </Route>
                {/* Sub route for Account */}
                <Route path="Account">
                  <Route
                    path="UserProfile"
                    element={<UserProfilePage />}
                  ></Route>
                  <Route
                    path="ChangePassword"
                    element={<ChangePassword />}
                  ></Route>
                </Route>
              </Route>
              {/* Default all unknown route to login page */}
              <Route path="*" element={<Navigate to="Dashboard" />} />
            </Routes>
          </BrowserRouter>
        </AntD>
      </IndexContext.Provider>
    </ConfigProvider>
  );
};

ReactDOM.createRoot(document.getElementById("root")).render(<App />);
